import React, { useState, useEffect } from "react";
import axios from "axios";
import { Tree, TreeNode } from "react-organizational-chart";
import { FamilyMember } from "types";
import {
  Switch,
  makeStyles,
  Theme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Snackbar,
  CircularProgress,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { loginUser, logout, useAuthState, useAuthDispatch } from "../context";
import Node from "./Node";
import SearchBar from "./SearchBar";
import { useParams } from "react-router";

const API_URL = "https://sheltered-ridge-72729.herokuapp.com/families";

const styles = makeStyles((theme: Theme) => ({
  root: {},
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "50vh",
  },
  switchWrapper: {
    display: "flex",
    alignItems: "center",
    position: "fixed",
    bottom: 20,
    fontSize: 12,
    left: 20,
    zIndex: 2000000,
  },
  switchButton: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255, 0.3);",
    borderRadius: 5,
    padding: 4,
    marginRight: 8,
  },
  ramahiImg: {
    marginTop: 8,
    width: 128,
  },
  ramahiImgEnglish: {
    marginTop: 8,
    width: 275,
  },
  title: {
    "& div": {
      display: "flex",
      justifyContent: "center",
      fontWeight: 700,
      position: "relative",
    },
  },
  textField: {
    width: 280,
    marginRight: 8,
    marginBottom: 8,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
  },
}));

const SingleFamilyTree = () => {
  const classes = styles();
  const [lang, setLang] = useState("ar");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [ids, setId] = useState<(string | number)[]>([]);
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [ancestorChain, setAncestorChain] = useState<FamilyMember[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [targetMember, setTargetMember] = useState<FamilyMember | null>(null);

  const userDetails = useAuthState();
  const { user = {} } = userDetails;
  const dispatch = useAuthDispatch();
  const { id } = useParams<{ id: string | undefined }>();

  // Fetch ancestor chain from target user up to root
  const fetchAncestorChain = async (memberId: number) => {
    setIsLoading(true);
    const chain: FamilyMember[] = [];
    let currentId: number | null = memberId;

    try {
      while (currentId !== null) {
        const response = await axios.get(`${API_URL}/${currentId}`);
        const member: FamilyMember = response.data;
        chain.unshift(member); // Add to beginning (building from bottom up)

        // Check if there's a parent
        if (member.parent && member.parent.length > 0 && member.parent[0]) {
          currentId = typeof member.parent[0] === 'object' 
            ? member.parent[0].id 
            : member.parent[0];
        } else {
          currentId = null; // Reached the root
        }
      }

      setAncestorChain(chain);
      setTargetMember(chain[chain.length - 1] || null);
    } catch (error) {
      console.error("Error fetching ancestor chain:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchAncestorChain(parseInt(id));
    }
  }, [id]);

  const updateData = (
    member: {
      name: string;
      arabicName: string;
      canEdit: boolean;
      canAdd: boolean;
      lineage: string;
      arabicLineage?: string;
    },
    parentId: number | string,
    updateFinish: () => void
  ) => {
    axios
      .post(API_URL, {
        ...member,
        parent: [parentId],
      })
      .then(() => {
        // Refresh the chain after adding
        if (id) fetchAncestorChain(parseInt(id));
        updateFinish();
      });
  };

  const editData = (
    memberId: number | string,
    member: {
      name: string;
      arabicName: string;
      canEdit: boolean;
      canAdd: boolean;
      lineage: string;
      arabicLineage?: string;
    },
    setFinished: () => void
  ) => {
    axios
      .put(`${API_URL}/${memberId}`, { ...member })
      .then(() => {
        if (id) fetchAncestorChain(parseInt(id));
        setFinished();
      });
  };

  const login = async (e: any) => {
    e.preventDefault();
    try {
      let response = await loginUser(dispatch, {
        identifier: userName,
        password: password,
      });
      if (!response.user) return;
      setShowLogin(false);
      setUserName("");
      setPassword("");
      setOpenAlert(true);
      setAlertMessage("Successfully logged in.");
    } catch (error) {
      console.log(error);
    }
  };

  const handleLogout = () => {
    logout(dispatch);
    setOpenAlert(true);
    setAlertMessage("Logged out.");
  };

  // Recursively render ancestor nodes from root down to target
  const renderAncestorTree = (ancestors: FamilyMember[], index: number): React.ReactNode => {
    if (index >= ancestors.length) return null;

    const member = ancestors[index];
    const isTarget = index === ancestors.length - 1;
    
    // Build lineage string for this node (names of ancestors above it)
    const lineageNames = ancestors.slice(0, index).map(a => a.name).reverse().join(" ");
    const arabicLineageNames = ancestors.slice(0, index).map(a => a.arabicName || a.name).reverse().join(" ");

    // If this is the target (last in chain), render with full Node component (shows children)
    if (isTarget) {
      return (
        <Node
          key={member.id}
          memberId={member.id}
          setData={updateData}
          isArabic={lang === "ar"}
          ids={ids}
          setId={setId}
          editUser={editData}
          length="0"
          lineage={lineageNames}
          arabicLineage={arabicLineageNames}
          color="#9575cd"
          singleNode
          branch={member.branch || ""}
        />
      );
    }

    // For ancestors, render a simpler TreeNode that contains the next level
    return (
      <TreeNode
        key={member.id}
        label={
          <div style={{ 
            color: "rgba(255,255,255,0.7)", 
            padding: "8px 16px",
            fontSize: 16,
            fontWeight: 500,
          }}>
            {lang === "ar" ? (member.arabicName || member.name) : member.name}
          </div>
        }
      >
        {renderAncestorTree(ancestors, index + 1)}
      </TreeNode>
    );
  };

  if (isLoading) {
    return (
      <>
        <SearchBar isArabic={lang === "ar"} />
        <div className={classes.loadingContainer}>
          <CircularProgress style={{ color: "white" }} />
        </div>
      </>
    );
  }

  return (
    <>
      <SearchBar isArabic={lang === "ar"} />
      <Tree
        label={
          <div>
            {lang === "en" ? (
              <div>
                <img
                  className={classes.ramahiImgEnglish}
                  src="/ramahiEnglish.png"
                  alt="Al Ramahi"
                />
              </div>
            ) : (
              <div>
                <img className={classes.ramahiImg} src="/ramahi.svg" alt="" />
              </div>
            )}
          </div>
        }
        lineColor={"white"}
      >
        {ancestorChain.length > 0 && renderAncestorTree(ancestorChain, 0)}
      </Tree>
      
      <div className={classes.switchWrapper}>
        <div className={classes.switchButton}>
          <div>AR</div>
          <Switch
            checked={lang === "en"}
            onChange={() => (lang === "en" ? setLang("ar") : setLang("en"))}
            size="small"
          />
          <div>EN</div>
        </div>
        <div className={classes.switchButton}>
          {Object.values(user).length > 0 ? (
            <Button onClick={handleLogout}>Logout</Button>
          ) : (
            <Button onClick={() => setShowLogin(true)}>Login</Button>
          )}
        </div>
        <Dialog
          open={showLogin}
          onClose={() => {
            setShowLogin(false);
            setUserName("");
            setPassword("");
          }}
          className={classes.root}
        >
          <DialogTitle className={classes.title}>Login</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <TextField
              id="outlined-basic"
              label="Username:"
              variant="outlined"
              size="small"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              className={classes.textField}
            />
            <TextField
              id="outlined-basic"
              label="Password:"
              variant="outlined"
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={classes.textField}
              type="password"
            />
            <Button onClick={login}>Login</Button>
          </DialogContent>
        </Dialog>
        <Snackbar
          open={openAlert}
          autoHideDuration={3000}
          onClose={() => setOpenAlert(false)}
        >
          <Alert severity="success" color="info">
            {alertMessage}
          </Alert>
        </Snackbar>
      </div>
    </>
  );
};

export default SingleFamilyTree;
